import { PROMPT_HELPER_GROUPS } from '../data/promptHelper.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';

const nsfwCategorySyncs = new Set();
let nsfwPrefListenerBound = false;

const ensureNsfwPrefListener = () => {
  if (nsfwPrefListenerBound) return;
  nsfwPrefListenerBound = true;
  document.addEventListener('nsfwPreferenceSelected', () => {
    nsfwCategorySyncs.forEach((sync) => sync());
  });
};

export const insertPromptAtCursor = (textareaEl, textToInsert, joinWith = ', ') => {
  if (!textareaEl || !textToInsert) return;

  const value = textareaEl.value || '';
  const start = textareaEl.selectionStart ?? value.length;
  const end = textareaEl.selectionEnd ?? value.length;

  const before = value.slice(0, start);
  const after = value.slice(end);
  const separator = typeof joinWith === 'string' && joinWith.length > 0 ? joinWith : ', ';
  const separatorTrimmed = separator.trim();

  const trimmedBefore = before.replace(/\s+$/, '');
  const alreadySeparated = separatorTrimmed
    ? trimmedBefore.endsWith(separatorTrimmed) || trimmedBefore.endsWith('\n')
    : !trimmedBefore;
  const needsSeparator = !!trimmedBefore && !alreadySeparated;
  const prefix = needsSeparator ? separator : trimmedBefore && !/\s$/.test(before) ? ' ' : '';

  const nextValue = `${before}${prefix}${textToInsert}${after}`;
  textareaEl.value = nextValue;

  const nextCursor = (before.length + prefix.length + textToInsert.length);
  textareaEl.setSelectionRange(nextCursor, nextCursor);
  textareaEl.focus();
  textareaEl.dispatchEvent(new Event('input'));
};

const toPillSpec = (tag) => {
  if (tag && typeof tag === 'object') {
    const label = String(tag.label || tag.insert || '').trim();
    return {
      label,
      insert: String(tag.insert || tag.label || '').trim(),
      onInsert: typeof tag.onInsert === 'function' ? tag.onInsert : null,
    };
  }
  const text = String(tag || '');
  return { label: text, insert: text, onInsert: null };
};

export const initPromptHelpers = ({
  helperRootId,
  pillsRootId,
  textareaId,
  customGroups = {},
  joinWith = ', ',
} = {}) => {
  const helperRoot = document.getElementById(helperRootId);
  const textareaEl = document.getElementById(textareaId);
  const pillsRoot = document.getElementById(pillsRootId);
  if (!helperRoot || !textareaEl || !pillsRoot) return;

  /** @type {HTMLElement[]} */
  const groupButtons = Array.from(helperRoot.querySelectorAll('[data-prompt-helper-group]')).filter((el) => el instanceof HTMLElement);
  if (!groupButtons.length) return;

  /** @type {null | string} */
  let activeGroup = null;

  const renderPills = (tags) => {
    pillsRoot.innerHTML = '';
    (Array.isArray(tags) ? tags : []).forEach((tag) => {
      const spec = toPillSpec(tag);
      if (!spec.label) return;
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'prompt-helper__pill';
      pill.textContent = spec.label;
      pill.setAttribute('aria-label', `Insert: ${spec.label}`);
      const handleInsert = () => {
        if (spec.onInsert) spec.onInsert();
        else insertPromptAtCursor(textareaEl, spec.insert, joinWith);
      };
      pill.addEventListener('click', handleInsert);
      pill.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        handleInsert();
      });
      pillsRoot.appendChild(pill);
    });
  };

  const syncActiveButtons = () => {
    groupButtons.forEach((btn) => {
      const isActive = btn.dataset.promptHelperGroup === activeGroup;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  const isNsfwGatedGroup = (group) => group === 'nsfw' || group === 'rating';

  const syncNsfwCategory = () => {
    const showNsfw = getNsfwEnabled();
    groupButtons.forEach((btn) => {
      if (!isNsfwGatedGroup(btn.dataset.promptHelperGroup)) return;
      btn.hidden = !showNsfw;
    });
    if (showNsfw || !isNsfwGatedGroup(activeGroup)) return;
    activeGroup = null;
    syncActiveButtons();
    pillsRoot.innerHTML = '';
  };

  const setActiveGroup = async (group) => {
    if (isNsfwGatedGroup(group) && !getNsfwEnabled()) return;

    const custom = customGroups[group];
    if (custom?.type === 'action') {
      custom.run?.();
      return;
    }

    const nextGroup = group && group !== activeGroup ? group : null;
    activeGroup = nextGroup;
    syncActiveButtons();
    pillsRoot.innerHTML = '';
    if (!activeGroup) return;

    const activeCustom = customGroups[activeGroup];
    if (activeCustom?.type === 'tags') {
      const tags = await Promise.resolve(activeCustom.getTags?.() || []);
      if (activeGroup !== nextGroup) return;
      renderPills(tags);
      return;
    }

    renderPills(PROMPT_HELPER_GROUPS[activeGroup] || []);
  };

  groupButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      void setActiveGroup(btn.dataset.promptHelperGroup || null);
    });
    btn.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      void setActiveGroup(btn.dataset.promptHelperGroup || null);
    });
  });

  nsfwCategorySyncs.add(syncNsfwCategory);
  ensureNsfwPrefListener();
  syncNsfwCategory();
};
